import React, {Suspense, useMemo} from "react";
import {BrowserRouter as Router, Routes, Route} from "react-router-dom";
import {QueryClient, QueryClientProvider} from "@tanstack/react-query";
import {ReactQueryDevtools} from "@tanstack/react-query-devtools";
import {ConfigProvider, theme, App as AntApp} from "antd";
import deDE from "antd/locale/de_DE";
import {ErrorBoundary, LoadingSpinner, Layout, ProtectedRoute} from "./components/layout";
import {ThemeProvider, useTheme} from "./contexts/ThemeContext";
import {AuthProvider} from "./contexts/AuthContext";
import "./styles/App.css";

// Lazy load components for better performance
const Dashboard = React.lazy(() => import("./pages/Dashboard"));
const Overview = React.lazy(() => import("./pages/Overview"));
const Runs = React.lazy(() => import("./pages/Runs"));
const RunDetail = React.lazy(() => import("./pages/RunDetail"));
const ExecutionDetail = React.lazy(() => import("./pages/ExecutionDetail"));
const Properties = React.lazy(() => import("./pages/Properties"));
const MyProperties = React.lazy(() => import("./pages/MyProperties"));
const AssignProperties = React.lazy(() => import("./pages/AssignProperties"));
const ScraperTest = React.lazy(() => import("./pages/ScraperTest"));
const Blacklists = React.lazy(() => import("./pages/Blacklists"));
const Employees = React.lazy(() => import("./pages/Employees"));
const EmployeeDetail = React.lazy(() => import("./pages/EmployeeDetail"));
const EmployeeProperties = React.lazy(() => import("./pages/EmployeeProperties"));
const Settings = React.lazy(() => import("./pages/Settings"));
const DocumentManagement = React.lazy(() => import("./pages/DocumentManagement"));
const Login = React.lazy(() => import("./pages/Login"));
const Focus = React.lazy(() => import("./pages/Focus"));
const SalesDashboard = React.lazy(() => import("./pages/SalesDashboard"));

// Configure React Query with optimized defaults
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: (failureCount, error: unknown) => {
                const apiError = error as { status?: number };
                if (
                    apiError?.status &&
                    apiError.status >= 400 &&
                    apiError.status < 500
                ) {
                    return false;
                }
                return failureCount < 3;
            },
            refetchOnWindowFocus: false,
            staleTime: 5 * 60 * 1000, // 5 minutes
            gcTime: 10 * 60 * 1000, // 10 minutes (was cacheTime)
        },
        mutations: {
            // Never retry mutations on their own: they are not idempotent. A run
            // execute that timed out after the server had accepted it would start
            // a second, paid crawl. The user sees the error and decides.
            retry: false,
        },
    },
});

const staticTokens = {
    colorPrimary: "#1890ff",
    colorSuccess: "#52c41a",
    colorError: "#ff4d4f",
    colorInfo: "#1890ff",
    borderRadius: 6,
    borderRadiusLG: 8,
    fontFamily:
        "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
};

// Component that provides theme-aware Ant Design configuration
const createTheme = (isDark: boolean) => ({
    algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
    token: {
        ...staticTokens,
        colorWarning: isDark ? "#faad14" : "#fa8c16",
        colorBgContainer: isDark ? "#1a1a1a" : "#ffffff",
        colorBgElevated: isDark ? "#1a1a1a" : "#ffffff",
        colorBgLayout: isDark ? "#141414" : "#f5f5f5",
        colorBgSpotlight: isDark ? "#1f1f1f" : "#fafafa",
        colorBorder: isDark ? "#303030" : "#f0f0f0",
        colorBorderSecondary: isDark ? "#434343" : "#e8e8e8",
        colorText: isDark ? "rgba(255, 255, 255, 0.85)" : "#262626",
        colorTextSecondary: isDark ? "rgba(255, 255, 255, 0.65)" : "#8c8c8c",
        colorTextTertiary: isDark ? "rgba(255, 255, 255, 0.45)" : "#bfbfbf",
        colorTextDisabled: isDark ? "rgba(255, 255, 255, 0.25)" : "#d9d9d9",
        colorFillSecondary: isDark ? "#1f1f1f" : "#fafafa",
        colorFillTertiary: isDark ? "#262626" : "#f5f5f5",
        boxShadow: isDark
            ? "0 2px 8px 0 rgba(0, 0, 0, 0.45)"
            : "0 2px 8px 0 rgba(0, 0, 0, 0.05)",
        boxShadowSecondary: isDark
            ? "0 4px 16px 0 rgba(0, 0, 0, 0.55)"
            : "0 4px 16px 0 rgba(0, 0, 0, 0.08)",
    },
    components: {
        Layout: {
            bodyBg: isDark ? "#141414" : "#f5f5f5",
            headerBg: isDark ? "#1a1a1a" : "#ffffff",
            siderBg: isDark ? "#1a1a1a" : "#ffffff",
            triggerBg: isDark ? "#1f1f1f" : "#fafafa",
        },
        Card: {
            borderRadiusLG: 8,
            colorBgContainer: isDark ? "#1a1a1a" : "#ffffff",
            colorBorderSecondary: isDark ? "#303030" : "#f0f0f0",
        },
        Button: {
            borderRadius: 6,
            colorPrimary: "#1890ff",
            colorPrimaryHover: "#40a9ff",
            colorPrimaryActive: isDark ? "#177ddc" : "#096dd9",
        },
        Menu: {
            colorBgContainer: isDark ? "#1a1a1a" : "#ffffff",
            colorItemBgSelected: "#111a2c",
            colorItemTextSelected: "#1890ff",
            colorItemBgHover: isDark ? "#1f1f1f" : "#fafafa",
            colorItemText: isDark ? "rgba(255, 255, 255, 0.85)" : "#262626",
        },
        Table: {
            colorBgContainer: isDark ? "#1a1a1a" : "#ffffff",
            headerBg: isDark ? "#1f1f1f" : "#fafafa",
            headerSortActiveBg: isDark ? "#1f1f1f" : "#fafafa",
            bodySortBg: isDark ? "#1a1a1a" : "#ffffff",
            colorBorderSecondary: isDark ? "#303030" : "#f0f0f0",
            rowHoverBg: isDark ? "#1f1f1f" : "#fafafa",
        },
        Modal: {
            colorBgElevated: isDark ? "#1a1a1a" : "#ffffff",
            contentBg: isDark ? "#1a1a1a" : "#ffffff",
        },
        Drawer: {
            colorBgElevated: isDark ? "#1a1a1a" : "#ffffff",
        },
        Input: {
            colorBgContainer: isDark ? "#141414" : "#ffffff",
            colorBorder: isDark ? "#303030" : "#f0f0f0",
        },
        Select: {
            colorBgContainer: isDark ? "#141414" : "#ffffff",
            colorBgElevated: isDark ? "#1a1a1a" : "#ffffff",
        },
        Tooltip: {
            colorBgSpotlight: isDark ? "rgba(0, 0, 0, 0.85)" : "rgba(0, 0, 0, 0.75)",
        },
    },
});

const protectedRoutes = [
    {path: "/", element: <Overview/>},
    {path: "/dashboard", element: <Dashboard/>},
    {path: "/overview", element: <Overview/>},
    {path: "/runs", element: <Runs/>},
    {path: "/runs/:id", element: <RunDetail/>},
    {path: "/executions/:id", element: <ExecutionDetail/>},
    {path: "/properties", element: <Properties/>},
    {path: "/my-properties", element: <MyProperties/>},
    {path: "/assign-properties", element: <AssignProperties/>},
    {path: "/focus", element: <Focus/>},
    {path: "/sales", element: <SalesDashboard/>},
    {path: "/blacklists", element: <Blacklists/>},
    {path: "/employees", element: <Employees/>},
    {path: "/employees/:employeeId", element: <EmployeeDetail/>},
    {
        path: "/employees/:employeeId/properties",
        element: <EmployeeProperties/>,
    },
    {path: "/scraper-test", element: <ScraperTest/>},
    {path: "/documents", element: <DocumentManagement/>},
    {path: "/settings", element: <Settings/>},
] as const;

const ThemedApp: React.FC = () => {
    const {isDark} = useTheme();

    // Memoize theme to prevent unnecessary re-renders
    const antdTheme = useMemo(() => createTheme(isDark), [isDark]);

    return (
        <ConfigProvider theme={antdTheme} locale={deDE}>
            <AntApp>
                <Router>
                    <Suspense fallback={<LoadingSpinner message="Loading..."/>}>
                        <Routes>
                            <Route path="/login" element={<Login/>}/>
                            <Route
                                path="/*"
                                element={
                                    <ProtectedRoute>
                                        <Layout>
                                            <Suspense
                                                fallback={<LoadingSpinner message="Loading page..."/>}
                                            >
                                                <Routes>
                                                    {protectedRoutes.map(({path, element}) => (
                                                        <Route key={path} path={path} element={element}/>
                                                    ))}
                                                </Routes>
                                            </Suspense>
                                        </Layout>
                                    </ProtectedRoute>
                                }
                            />
                        </Routes>
                    </Suspense>
                </Router>
            </AntApp>
        </ConfigProvider>
    );
};

function App() {
    return (
        <ErrorBoundary>
            <AuthProvider>
                <ThemeProvider>
                    <QueryClientProvider client={queryClient}>
                        <ThemedApp/>
                        {import.meta.env.DEV && (
                            <ReactQueryDevtools
                                initialIsOpen={false}
                                buttonPosition="bottom-right"
                            />
                        )}
                    </QueryClientProvider>
                </ThemeProvider>
            </AuthProvider>
        </ErrorBoundary>
    );
}

export default App;
